// [Portfolio status] > [<specialist>] - one card per project the
// specialist keeps a status row for, in that specialist's own vocabulary.
// docs/Y2026-Q3/design-portfolio-daily-runs.md section 5.4; an opened card is the
// project's specialist tab (docs/Y2026-Q4/design-standard-specialist-surfaces.md).
//
// Which projects it works is set on the team's Settings tab.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  type PortfolioEntry,
  type RowVocabulary,
  useAgentPortfolio,
  useDayManifest,
} from "../api/hooks/useAgentPortfolio";
import { useStatefulAgents } from "../api/hooks/useStatefulAgents";
import {
  LifecycleChip,
  OwnerChip,
  VerdictFlag,
} from "../components/PortfolioRowParts";
import { SpecialistDetails } from "./ProjectSpecialistTab";
import { useProjectAgentStatus } from "../api/hooks/useProjectAgentStatus";
import { ReviewProvider } from "../components/ReviewProvider";
import { reviewAttrs } from "../utils/reviewState";
import styles from "./AgentPortfolioTab.module.css";
import {
  projectAgentStatePath,
  projectScheduledPath,
  projectSpecialistPath,
  teamSettingsPath,
} from "./learningAgentPaths";
import { Chevron } from "../components/Chevron";

const formatDay = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "-";

type SortKey = "name" | "stage" | "wait";

// Rows with nothing to sort on go last, whichever way.
const ascending = (a: string, b: string): number =>
  a && b ? a.localeCompare(b) : a ? -1 : b ? 1 : 0;

// By name, "where is site X?" is a lookup; by stage, in the agent's own order of
// progress, the portfolio reads as a pipeline; by wait, the moves owed longest on us
// lead, then the longest waits on others.
const sorter =
  (key: SortKey, vocabulary: RowVocabulary) =>
  (a: PortfolioEntry, b: PortfolioEntry): number => {
    const byName = a.project_name.localeCompare(b.project_name);
    if (key === "stage") {
      const rank = (e: PortfolioEntry): number => {
        const at = vocabulary.lifecycle.indexOf(e.row?.lifecycle ?? "");
        return at === -1 ? vocabulary.lifecycle.length : at;
      };
      return rank(a) - rank(b) || byName;
    }
    if (key === "wait") {
      const onUs = (e: PortfolioEntry) => (e.next_move.who === "US" ? 0 : 1);
      // Longest held up first.
      const since = (e: PortfolioEntry) => e.next_move.since || "9999";
      return onUs(a) - onUs(b) || ascending(since(a), since(b)) || byName;
    }
    return byName;
  };

const ProjectCard = ({
  entry,
  agent,
  label,
  open,
  onToggle,
}: {
  entry: PortfolioEntry;
  agent: string;
  label: string;
  open: boolean;
  onToggle: () => void;
}) => {
  const row = entry.row;
  // Open, the card shows the specialist's actions as just loaded, so its chip reads the
  // next move that came with them.
  const { data: status } = useProjectAgentStatus(
    open ? entry.project_id : null,
  );
  const move =
    status?.agents.find((a) => a.agent === agent)?.lists?.next_move ??
    entry.next_move;
  return (
    <section
      // A comment selected in the card is on this specialist's file for this project.
      {...reviewAttrs(
        "specialist_details",
        `${label} on ${entry.project_name}`.slice(0, 200),
        { agent, doc: `project:${entry.project_id}` },
      )}
      className={
        open
          ? `${styles.projectCard} ${styles.projectCardOpen}`
          : styles.projectCard
      }
    >
      <div className={styles.projectHead}>
        <button
          type="button"
          className={styles.projectToggle}
          onClick={onToggle}
          aria-expanded={open}
        >
          <span className={styles.caret} aria-hidden="true">
            <Chevron tree open={open} />
          </span>
          <span className={styles.projectName}>{entry.project_name}</span>
          {/* Lifted from the workbook, not yet grounded in documents the agent
              read itself. */}
          {row?.seeded && <span className={styles.seeded}>seeded</span>}
          {entry.attention && (
            <span className={styles.attentionBadge} title={entry.attention}>
              needs attention
            </span>
          )}
          {row && <LifecycleChip lifecycle={row.lifecycle} />}
          {row && (
            <OwnerChip
              who={move.who}
              lifecycle={row.lifecycle}
              overdueFrom={move.overdue_from}
              move={move}
            />
          )}
          <span className={styles.updated}>{formatDay(entry.updated_at)}</span>
        </button>
      </div>
      {entry.glance && (
        <p className={styles.glance}>
          <VerdictFlag onTrack={row?.on_track} /> {entry.glance.line}
        </p>
      )}
      {!open && row && (
        <div className={styles.summary}>
          {entry.glance?.opener && <p>{entry.glance.opener}</p>}
        </div>
      )}
      {!open && !row && (
        <p className={`${styles.summary} ${styles.muted}`}>
          No status yet - the next scheduled pass on this project writes one.
        </p>
      )}
      {open && (
        <div className={styles.projectBody}>
          {/* The project's own specialist tab, so every specialist's card shows its
              whole file: readiness areas, contracts, decisions, history and lists. */}
          <SpecialistDetails projectId={entry.project_id} agent={agent} ownPage={false} />
          <p className={styles.provenance}>
            {row && (
              <>
                Row v{entry.version}, written {formatDay(entry.updated_at)}
                {" · "}
              </>
            )}
            {entry.latest_run_id && (
              <>
                <Link
                  to={projectScheduledPath(
                    entry.project_id,
                    entry.latest_run_id,
                  )}
                >
                  Discuss on the latest run
                </Link>
                {" · "}
              </>
            )}
            <Link to={projectSpecialistPath(entry.project_id, agent)}>
              {label} tab on the project
            </Link>
            {" · "}
            <Link to={projectAgentStatePath(entry.project_id, agent)}>
              {label}&apos;s full record
            </Link>
          </p>
        </div>
      )}
    </section>
  );
};

// Keyed by agent: the route keeps one instance across agents, and a filter in one
// agent's vocabulary matches nothing in another's.
export const AgentPortfolioTab = () => {
  const { agent } = useParams();
  return <AgentPortfolio key={agent} agent={agent} />;
};

const AgentPortfolio = ({ agent }: { agent: string | undefined }) => {
  const portfolio = useAgentPortfolio(agent ?? null);
  const roster = useStatefulAgents();
  const isOptional = Boolean(roster.data?.agents.find((a) => a.agent === agent)?.optional);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [changedOnly, setChangedOnly] = useState(false);
  // From the day manifest: a second definition of "changed today" would drift.
  const manifest = useDayManifest(agent ?? null);
  const movedToday = new Set(
    (manifest.data?.projects ?? [])
      .filter((p) => p.moved)
      .map((p) => p.project_id),
  );

  if (portfolio.isLoading)
    return <p className={styles.muted}>Loading the portfolio…</p>;
  if (portfolio.error)
    return <p className={styles.error}>Could not load the portfolio.</p>;
  const data = portfolio.data;
  if (!data || !agent) return null;

  const order = sorter(sortKey, data.vocabulary);
  const enrolled = [...data.enrolled]
    .filter((e) => !ownerFilter || e.next_move.who === ownerFilter)
    .filter((e) => !stageFilter || e.row?.lifecycle === stageFilter)
    .filter((e) => !changedOnly || movedToday.has(e.project_id))
    .sort(order);
  const shownIds = enrolled.map((e) => e.project_id);
  const allOpen = shownIds.length > 0 && shownIds.every((id) => open.has(id));

  const toggle = (projectId: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

  const cards = (entries: PortfolioEntry[]) => (
    <div className={styles.cards}>
      {entries.map((entry) => (
        <ProjectCard
          key={entry.project_id}
          entry={entry}
          agent={agent}
          label={data.label}
          open={open.has(entry.project_id)}
          onToggle={() => toggle(entry.project_id)}
        />
      ))}
    </div>
  );

  return (
    // Each card names its project, so a comment goes to that project's specialist.
    <ReviewProvider
      available
      defaultDoc={null}
      defaultLevel="specialist_details"
      comments={[]}
      projectId={null}
      onQuote={null}
    >
      <div className={styles.tab}>
        <div className={styles.controls}>
          <label>
            Stage
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <option value="">any</option>
              {data.vocabulary.lifecycle.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Next step owner
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
            >
              <option value="">any</option>
              <option value="US">on us</option>
              <option value="THEM">with them</option>
              <option value="NOBODY">nobody</option>
            </select>
          </label>
          <label>
            Sort by
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
              <option value="name">name</option>
              <option value="stage">stage</option>
              <option value="wait">longest wait on us</option>
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={changedOnly}
              onChange={(e) => setChangedOnly(e.target.checked)}
            />{" "}
            Changed today
          </label>
          <span className={styles.spacer} />
          <span className={styles.muted}>
            {enrolled.length} of {data.enrolled.length} enrolled
          </span>
          <button
            type="button"
            className={styles.link}
            disabled={shownIds.length === 0}
            onClick={() => setOpen(allOpen ? new Set() : new Set(shownIds))}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>

        {data.enrolled.length === 0 ? (
          <p className={styles.muted}>
            No project is enrolled with {data.label} yet.{" "}
            {isOptional ? (
              <>
                Switch it on from the team&apos;s{" "}
                <Link to={teamSettingsPath}>Settings</Link> tab.
              </>
            ) : (
              "It works every project whose scheduled runs are on: turn a project's runs on from its own Settings tab."
            )}
          </p>
        ) : enrolled.length === 0 ? (
          <p className={styles.muted}>
            No enrolled project matches these filters.
          </p>
        ) : (
          cards(enrolled)
        )}

      </div>
    </ReviewProvider>
  );
};
